// Couche de données du module client « Mes paiements ».
//
// Source de vérité : Supabase. L'isolation par compte est garantie par les
// politiques RLS existantes (`rls_paiements_abonnements_select` et
// `rls_abonnements_select`) : le client connecté ne lit QUE ses propres
// paiements. Aucun identifiant de compte n'est fourni par le navigateur et
// ces fonctions sont en lecture seule (aucune écriture, aucun service_role).

import { supabase } from "@/store/shared/supabase";
import type { MonPaiementAbonnement, PaiementAbonnementStatut } from "./types";

type Row = Record<string, unknown>;

const str = (value: unknown): string => (typeof value === "string" ? value : "");
const num = (value: unknown): number => (typeof value === "number" ? value : Number(value ?? 0) || 0);
const rows = (value: unknown): Row[] => (Array.isArray(value) ? (value as Row[]) : []);

const STATUTS: PaiementAbonnementStatut[] = ["en_attente", "valide", "rejete", "annule"];

function statutFromDb(value: string): PaiementAbonnementStatut {
  return (STATUTS as string[]).includes(value) ? (value as PaiementAbonnementStatut) : "en_attente";
}

/* ------------------------------------------------------------------ */
/* Libellés d'affichage (aucune donnée n'est réécrite en base)         */
/* ------------------------------------------------------------------ */

const METHODE_LABELS: Record<string, string> = {
  orange_money: "Orange Money",
  moov_money: "Moov Money",
  especes: "Espèces",
};

const METHODE_ICONS: Record<string, string> = {
  orange_money: "ri-smartphone-line",
  moov_money: "ri-smartphone-line",
  especes: "ri-money-cny-circle-line",
};

/**
 * Libellé du moyen de paiement. Les trois moyens autorisés ont leur libellé
 * officiel ; une valeur historique inconnue (ex. `autre`) est présentée de façon
 * neutre, telle qu'enregistrée, SANS être transformée ni réécrite en base.
 */
export function methodeLabel(value: string): string {
  const known = METHODE_LABELS[value];
  if (known) return known;
  const raw = value.trim();
  if (!raw) return "—";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function methodeIcon(value: string): string {
  return METHODE_ICONS[value] ?? "ri-wallet-3-line";
}

/** La preuve de paiement s'applique uniquement à Orange Money et Moov Money. */
export function methodeExigePreuve(value: string): boolean {
  return value === "orange_money" || value === "moov_money";
}

export function periodiciteLabel(value: string): string {
  if (value === "annuelle") return "Annuelle";
  if (value === "mensuelle") return "Mensuelle";
  return "—";
}

/* ------------------------------------------------------------------ */
/* Lecture                                                             */
/* ------------------------------------------------------------------ */

/**
 * Récupère les paiements d'abonnement du compte connecté.
 * Le RLS filtre automatiquement par compte : aucune donnée tierce n'est exposée.
 */
export async function fetchMesPaiements(): Promise<MonPaiementAbonnement[]> {
  const [aboRes, payRes, formulesRes] = await Promise.all([
    supabase.from("abonnements").select("id, formule_id, periodicite"),
    supabase
      .from("paiements_abonnements")
      .select(
        "id, abonnement_id, montant, mode_paiement, reference, statut, preuve_url, date_paiement, date_validation, commentaire, created_at"
      )
      .order("created_at", { ascending: false }),
    supabase.from("formules").select("id, code, nom"),
  ]);

  const firstError = [aboRes, payRes, formulesRes].map((result) => result.error).find(Boolean);
  if (firstError) throw firstError;

  const aboById = new Map(rows(aboRes.data).map((row) => [str(row.id), row]));
  const formuleById = new Map(rows(formulesRes.data).map((row) => [str(row.id), row]));

  return rows(payRes.data).map((row) => {
    const abo = aboById.get(str(row.abonnement_id));
    const formule = abo ? formuleById.get(str(abo.formule_id)) : undefined;
    const datePaiement = str(row.date_paiement);
    const createdAt = str(row.created_at);
    const dateValidation = str(row.date_validation);

    return {
      id: str(row.id),
      montant: num(row.montant),
      mode: str(row.mode_paiement),
      reference: str(row.reference),
      statut: statutFromDb(str(row.statut)),
      preuveUrl: str(row.preuve_url),
      dateSoumission: datePaiement || createdAt,
      dateValidation: dateValidation || null,
      motif: str(row.commentaire),
      formuleCode: formule ? str(formule.code) : "",
      formuleNom: formule ? str(formule.nom) : "",
      periodicite: abo ? str(abo.periodicite) : "",
    } satisfies MonPaiementAbonnement;
  });
}